import { BASE } from './base';

/** localStorage that never throws (private mode, blocked storage, quota). */
/** Demo and product keep separate settings on the same device. */
const ns = (key: string) => (BASE ? `demo:${key}` : key);

export const storage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(ns(key));
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.localStorage.setItem(ns(key), value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(ns(key));
    } catch {
      /* ignore */
    }
  },
  getJSON<T>(key: string): T | null {
    const v = storage.get(key);
    if (!v) return null;
    try {
      return JSON.parse(v) as T;
    } catch {
      return null;
    }
  },
  setJSON(key: string, value: unknown): void {
    storage.set(key, JSON.stringify(value));
  },
};

export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // RFC 4122 v4 fallback for older WebViews.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Stable per-browser id so every write is stamped with the device (PRD Security). */
export function deviceId(): string {
  let id = storage.get('clear.device');
  if (!id) {
    id = uuid();
    storage.set('clear.device', id);
  }
  return id;
}

export interface RememberedUser {
  id: string;
  name: string;
  title: string;
  photoUrl: string | null;
  roles: string[];
}
export const rememberUser = (u: RememberedUser | null) =>
  u ? storage.setJSON('clear.lastUser', u) : storage.remove('clear.lastUser');
export const rememberedUser = () => storage.getJSON<RememberedUser>('clear.lastUser');
